import {
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import ClearCartConfirmModal from '~/components/ClearCartConfirmModal';
import DatePicker from '~/components/DatePicker';
import PickupDaySelect from './PickupDaySelect';
import PickupDurationSelect from './PickupDurationSelect';

import { IoTimeOutline } from 'react-icons/io5';

import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import ordersAtom from '~/recoil/atom/orders';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import platformAtom from '~/recoil/atom/platform';
import currentPickMealPointSelector from '~/recoil/selectors/currentPickMealPoint';

import useCartId from '~/hooks/useCartId';
import useStoreList from '~/hooks/useStoreList';
import usePickMealTime from './hooks/usePickMealTime';

import {
  addDays,
  addHours,
  addMinutes,
  differenceInDays,
  format,
  isAfter,
  isSameDay,
  isToday,
  startOfDay,
} from 'date-fns';

import { weekLabel } from '~/types/utilities';

import * as fp from 'lodash/fp';

import { DayType } from '~/components/DatePicker/type';
import { Week } from '~/types/common';

//TODO: 月曆畫面與功能
export type PickupSettingModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

enum DatePickerType {
  CALENDAR = 'calendar',
  WEEK = 'week',
}

export default function PickupSettingModal({ isOpen, onClose }: PickupSettingModalProps) {
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);

  const platform = useRecoilValue(platformAtom);
  const currentPickMealPoint = useRecoilValue(currentPickMealPointSelector);
  const pickMealAt = useRecoilValue(pickMealAtAtom);
  const pickMealEnd = useRecoilValue(pickMealEndAtom);
  const orders = useRecoilValue(ordersAtom);

  const { cartId } = useCartId();

  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: onConfirmModalClose,
  } = useDisclosure();

  const [datePickerType, setDatePickerType] = useState(DatePickerType.WEEK);

  const availableStoreIds = useMemo(() => {
    return currentPickMealPoint.stores.filter((id) => platform.displayStores.includes(id));
  }, [platform.displayStores, currentPickMealPoint.stores]);

  const { storeList } = useStoreList(availableStoreIds);

  const {
    pickMealDay,
    pickMealTimeMin,
    handleChangePickMealDayAt,
    handleChangePickMealTime,
    handleUpdatePickMealTime,
    handleResetTime,
    todayOrderAfterHours,
    earliestPickMealDate,
  } = usePickMealTime();

  const handleToggleDatePickerType = () => {
    switch (datePickerType) {
      case DatePickerType.WEEK: {
        return setDatePickerType(DatePickerType.CALENDAR);
      }
      case DatePickerType.CALENDAR: {
        return setDatePickerType(DatePickerType.WEEK);
      }
    }
  };

  const handleConfirmChangeTime = async () => {
    try {
      const res = await handleUpdatePickMealTime(cartId);
      if (res) {
        onConfirmModalClose();
        onClose();
      }
    } catch (err) {
      console.error('update pick meal time error');
    }
  };

  const handleCheckPickMealTime = async () => {
    const { pickMealAt: userPickMealAt } = pickMealDay;
    if (!userPickMealAt || !pickMealTimeMin) {
      return false;
    }

    const combinedPickMealTime = {
      at: addMinutes(startOfDay(userPickMealAt), pickMealTimeMin.pickMealAtTimeMin),
      end: addMinutes(startOfDay(userPickMealAt), pickMealTimeMin.pickMealEndTimeMin),
    };

    // 比對原始資料是否相同
    if (
      fp.eq(pickMealAt?.getTime(), combinedPickMealTime.at.getTime()) &&
      fp.eq(pickMealEnd?.getTime(), combinedPickMealTime.end.getTime())
    ) {
      onClose();
      return true;
    }

    // 檢查購物車是否為空
    if (fp.lte(orders.length, 0)) {
      try {
        const res = await handleUpdatePickMealTime(cartId);
        if (res) {
          onConfirmModalClose();
          onClose();
        }
        return;
      } catch (err) {
        console.error('update pickMealTime error', err);
        return;
      }
    }

    onConfirmModalOpen();
  };

  useEffect(() => {
    if (isOpen) {
      handleResetTime();
    }
  }, [isOpen]);

  // preOrder time start to end range
  const pickMealTimeRange = useMemo(() => {
    const now = new Date();
    return {
      startTime: addHours(
        now,
        fp.eq(storeList.length, 0)
          ? platform.preOrderSetting.afterHours
          : Math.min(...storeList.map(({ preOrderSetting: { afterHours } }) => afterHours)),
      ),
      endTime: addHours(
        now,
        fp.eq(storeList.length, 0)
          ? platform.preOrderSetting.beforeHours
          : Math.max(...storeList.map(({ preOrderSetting: { beforeHours } }) => beforeHours)),
      ),
    };
  }, [platform.preOrderSetting, storeList]);

  // calendar display day type fn
  const handleDayRuleFn = useCallback(
    (date: Date) => {
      const { startTime, endTime } = pickMealTimeRange;

      if (
        fp.lte(date.getTime(), startTime.getTime()) ||
        fp.gt(date.getTime(), endTime.getTime()) ||
        isAfter(date, earliestPickMealDate)
      ) {
        return DayType.DISABLE;
      }

      const week = format(date, 'EEE').toUpperCase() as Week;

      if (fp.lte(currentPickMealPoint.operatingTime[week] || [].length, 0)) {
        return DayType.DISABLE;
      }

      const { pickMealAt } = pickMealDay;
      if (pickMealAt && isSameDay(pickMealAt, date)) {
        return DayType.ACTIVE;
      }

      return DayType.NORMAL;
    },
    [pickMealTimeRange, pickMealDay, currentPickMealPoint.operatingTime, earliestPickMealDate],
  );

  // generator week list date
  const weekDayDisplayList = useMemo(() => {
    const { startTime, endTime } = pickMealTimeRange;
    const diffDays = differenceInDays(startOfDay(endTime), startOfDay(startTime));

    const rangeDates = fp
      .times((index) => {
        const date = addDays(startTime, index);
        const week = format(date, 'EEE').toUpperCase() as Week;

        if (fp.lte((currentPickMealPoint.operatingTime[week] || []).length, 0)) {
          return null;
        }

        return date;
      })(diffDays)
      .filter((date) => fp.isDate(date)) as Array<Date>;

    return rangeDates.slice(0, 5);
  }, [pickMealTimeRange, currentPickMealPoint.operatingTime]);

  // generator time selector list
  const timeDurationList = useMemo(() => {
    const { pickMealAt } = pickMealDay;

    if (!pickMealAt) {
      return [];
    }
    const { startTime, endTime } = pickMealTimeRange;

    const week = format(pickMealAt, 'EEE').toUpperCase() as Week;

    const schedules = [...currentPickMealPoint.operatingTime[week]];

    return schedules
      .filter(({ start }) => {
        const scheduleTime = addMinutes(startOfDay(pickMealAt), start);
        return (
          fp.gt(scheduleTime.getTime(), startTime.getTime()) &&
          fp.lte(scheduleTime.getTime(), endTime.getTime())
        );
      })
      .filter((d) => {
        return pickMealAt && isToday(pickMealAt)
          ? new Date().getHours() * 60 + new Date().getMinutes() <
              d.start - todayOrderAfterHours * 60
          : true;
      })
      .sort((a, b) => a.start - b.start);
  }, [currentPickMealPoint.operatingTime, pickMealDay, pickMealTimeRange, todayOrderAfterHours]);

  // current select time string
  const selectTimeString = (() => {
    const { pickMealAt } = pickMealDay;
    if (!pickMealAt) {
      return '-';
    }

    const dateString = format(pickMealAt, 'MM/dd');
    const weekString = weekLabel[pickMealAt.getDay()].label || '-';

    if (!pickMealTimeMin) {
      return `${dateString} (${weekString}) --:-- ~ --:--`;
    }

    const startTime = addMinutes(startOfDay(pickMealAt), pickMealTimeMin.pickMealAtTimeMin);
    const endTime = addMinutes(startOfDay(pickMealAt), pickMealTimeMin.pickMealEndTimeMin);

    return `${dateString} (${weekString}) ${format(startTime, 'HH:mm')} ~ ${format(
      endTime,
      'HH:mm',
    )}`;
  })();

  return (
    <>
      {isLargerThan768 ? (
        <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent m={10} h="95%">
            <ModalHeader borderBottom="1px solid" borderBottomColor="primary">
              <Center mt={4}>
                <HStack spacing={3}>
                  <Icon as={IoTimeOutline} color="primary" boxSize="1.5rem" />
                  <Text fontSize="xl" fontWeight="normal">
                    取餐設定
                  </Text>
                </HStack>
              </Center>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody p={0}>
              <VStack p={6} align="stretch">
                <Text fontSize="sm">預定時間</Text>
                <Text color={pickMealTimeMin ? 'fontColor' : 'danger.600'}>{selectTimeString}</Text>

                <Divider />

                <HStack justify="space-between" align="flex-start" my={3}>
                  <Text fontSize="xs" color="gray.700">
                    最遠可預訂{differenceInDays(earliestPickMealDate, new Date())}天後
                  </Text>
                  <Text fontSize="xs" color="fontColor" onClick={handleToggleDatePickerType}>
                    {datePickerType === DatePickerType.WEEK && '顯示月曆'}
                    {datePickerType === DatePickerType.CALENDAR && '顯示日曆'}
                  </Text>
                </HStack>

                {datePickerType === DatePickerType.WEEK && (
                  <PickupDaySelect
                    dayList={weekDayDisplayList}
                    date={pickMealDay.pickMealAt}
                    onChangeDate={handleChangePickMealDayAt}
                  />
                )}

                {datePickerType === DatePickerType.CALENDAR && (
                  <DatePicker
                    date={pickMealDay.pickMealAt}
                    dayRuleFn={handleDayRuleFn}
                    onChangeDate={handleChangePickMealDayAt}
                  />
                )}

                <PickupDurationSelect
                  durationList={timeDurationList}
                  pickMealTime={pickMealTimeMin}
                  onChangePickMealTime={handleChangePickMealTime}
                />
              </VStack>
            </ModalBody>

            <ModalFooter p={0}>
              <Button
                h="3.5rem"
                onClick={handleCheckPickMealTime}
                variant="unstyled"
                bg="primary"
                borderRadius="unset"
                color="white"
                isFullWidth
              >
                確認
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ) : (
        <Drawer isOpen={isOpen} onClose={onClose} placement="left" size="xl">
          <DrawerOverlay />
          <DrawerContent h="full">
            <DrawerHeader borderBottom="1px solid" borderBottomColor="primary">
              <Center mt={4}>
                <HStack spacing={3}>
                  <Icon as={IoTimeOutline} color="primary" boxSize="1.5rem" />
                  <Text fontSize="xl" fontWeight="normal">
                    取餐設定
                  </Text>
                </HStack>
              </Center>
            </DrawerHeader>
            <DrawerCloseButton />
            <DrawerBody p={4}>
              <VStack align="stretch">
                <Text fontSize="sm">預定時間</Text>
                <Text>{selectTimeString}</Text>

                <Divider />

                <HStack justify="space-between" align="flex-start" my={3}>
                  <Text fontSize="xs" color="gray.700">
                    最遠可預訂{differenceInDays(earliestPickMealDate, new Date())}天後
                  </Text>
                  <Text
                    cursor="pointer"
                    fontSize="xs"
                    color="fontColor"
                    onClick={handleToggleDatePickerType}
                  >
                    {datePickerType === DatePickerType.WEEK && '顯示月曆'}
                    {datePickerType === DatePickerType.CALENDAR && '顯示日曆'}
                  </Text>
                </HStack>

                {datePickerType === DatePickerType.WEEK && (
                  <PickupDaySelect
                    dayList={weekDayDisplayList}
                    date={pickMealDay.pickMealAt}
                    onChangeDate={handleChangePickMealDayAt}
                  />
                )}

                {datePickerType === DatePickerType.CALENDAR && (
                  <DatePicker
                    date={pickMealDay.pickMealAt}
                    dayRuleFn={handleDayRuleFn}
                    onChangeDate={handleChangePickMealDayAt}
                  />
                )}

                <PickupDurationSelect
                  durationList={timeDurationList}
                  pickMealTime={pickMealTimeMin}
                  onChangePickMealTime={handleChangePickMealTime}
                />
              </VStack>
            </DrawerBody>
            <DrawerFooter p={0}>
              <Button
                h="3.5rem"
                onClick={handleCheckPickMealTime}
                variant="unstyled"
                bg="primary"
                borderRadius="unset"
                color="white"
                isFullWidth
              >
                確認
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
      <ClearCartConfirmModal
        isOpen={isConfirmModalOpen}
        title="確定改變取餐時間？"
        onClose={onConfirmModalClose}
        onConfirm={handleConfirmChangeTime}
      >
        <VStack align="center">
          <Text textAlign="center" fontSize="sm" color="danger.600">
            改變取餐時間，將同時改變您的購物車
          </Text>
        </VStack>
      </ClearCartConfirmModal>
    </>
  );
}
